import L from 'leaflet';
import React from 'react';
import { MapContainer, ImageOverlay, useMapEvents, Marker } from 'react-leaflet';
import * as Inovx from '@shared';
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

type MarkerColor = 'blue' | 'red' | 'green';

interface FacilityMapPreviewProps {
    siteMap: ISiteMap;
    editable: boolean;
    onOriginChange?: (origin: any, marker: MarkerColor) => void;
}

type ImageData = {
    src: string;
    width: number;
    height: number;
};

const FacilityMapPreview: React.FC<FacilityMapPreviewProps> = (props) => {
    const [imageData, setImageData] = React.useState<ImageData | null>(null);
    
    React.useEffect(() => {
        const imageUrl = props.siteMap.MapPreview ?? `${UrlUtilities.getBaseApplicationUrl()}${props.siteMap.MapFileImageUrl}`;
        const image = new Image();

        image.onload = () => {
            setImageData({
                src: imageUrl,
                width: image.width,
                height: image.height,
            });
        };

        image.onerror = () => {
            console.error('Failed to load image');
            // Handle error appropriately
        };

        image.src = imageUrl;
    }, []);


    // DraggableMarker now accepts a 'color' prop, 'lat', 'lng', and 'draggable' props
    interface DraggableMarkerProps {
        color: MarkerColor;
        lat: number;
        lng: number;
        draggable?: boolean;
    }

    const DraggableMarker: React.FC<DraggableMarkerProps> = ({ color, lat, lng, draggable = true }) => {
        // Only customize icon for red and green, use default for blue
        let customIcon = undefined;
        if (color === 'red' || color === 'green') {
            const iconPaths: Record<string, string> = {
                red: 'marker-icon-red.png',
                green: 'marker-icon-green.png',
            };
            const iconRetinaPaths: Record<string, string> = {
                red: 'marker-icon-red-2x.png',
                green: 'marker-icon-green-2x.png',
            };
            customIcon = new L.Icon.Default({
                iconUrl: iconPaths[color],
                iconRetinaUrl: iconRetinaPaths[color], // Override retina icon as well
            });
        }

        const [position, setPosition] = React.useState({ lat, lng });
        React.useEffect(() => {
            setPosition({ lat, lng });
        }, [lat, lng]);

        // Only enable map click and dragend handlers if draggable
        useMapEvents(
            draggable
                ? {
                    click: (e) => {
                        setPosition(e.latlng);
                        props.onOriginChange(e.latlng, color)
                    }
                }
                : {}
        );

        const eventHandlers = draggable
            ? {
                dragend: (event: any) => {
                    const newLatLng = event.target.getLatLng();
                    props.onOriginChange(newLatLng, color)
                }
            }
            : undefined;

        return position === null ? null : (
            <Marker
                position={position}
                draggable={draggable}
                {...(customIcon ? { icon: customIcon } : {})}
                {...(eventHandlers ? { eventHandlers } : {})}
            />
        );
    };

    if (!imageData) {
        return <div>{InovxLoader.t('common:message.loading')}</div>;
    }
    else {
        const imageBounds: L.LatLngBoundsLiteral = [[-imageData.height / 2, -imageData.width / 2], [imageData.height / 2, imageData.width / 2]];
        const imageCenter: L.LatLngTuple = [0.0, 0.0];
        const zoom = 0;

        return (
            <MapContainer
                crs={L.CRS.Simple}
                center={imageCenter}
                zoom={zoom}
                style={{ width: '100%', height: '100%' }}
                maxBounds={imageBounds}
                maxBoundsViscosity={1.0}
                maxZoom={5}
                minZoom={-5}
                attributionControl={false}
            >
                <ImageOverlay url={imageData.src} bounds={imageBounds} />
                <DraggableMarker color="blue" lat={props.siteMap.ImageTranslation.Y} lng={props.siteMap.ImageTranslation.X} draggable={props.editable} />
                <DraggableMarker color="green" lat={props.siteMap.ImageScale.Y} lng={props.siteMap.ImageScale.X} draggable={props.editable} />
            </MapContainer>
        );
    }
}

export default FacilityMapPreview;
